import React, { useState, useEffect } from 'react';
import { sitesApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Building2, Plus, MapPin, Edit2, Trash2, Upload, Sun, Moon, X, Image } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SitesPage() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', location: '', description: '' });
  const [showLogoDialog, setShowLogoDialog] = useState(null); // site_id
  const [uploadingLight, setUploadingLight] = useState(false);
  const [uploadingDark, setUploadingDark] = useState(false);

  const fetchSites = async () => {
    try {
      const { data } = await sitesApi.list();
      setSites(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchSites(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await sitesApi.update(editing, form);
      } else {
        await sitesApi.create(form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', location: '', description: '' });
      fetchSites();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this site?')) return;
    await sitesApi.delete(id);
    fetchSites();
  };

  const openEdit = (site) => {
    setForm({ name: site.name, location: site.location || '', description: site.description || '' });
    setEditing(site._id);
    setShowForm(true);
  };

  const handleLogoUpload = async (file, mode) => {
    if (!showLogoDialog || !file) return;
    const setter = mode === 'light' ? setUploadingLight : setUploadingDark;
    setter(true);
    try {
      await sitesApi.uploadLogo(showLogoDialog, mode, file);
      toast.success(`${mode === 'light' ? 'Light' : 'Dark'} mode logo uploaded`);
      fetchSites();
    } catch {
      toast.error('Logo upload failed');
    }
    setter(false);
  };

  const handleLogoDelete = async (mode) => {
    if (!showLogoDialog) return;
    try {
      await sitesApi.deleteLogo(showLogoDialog, mode);
      toast.success(`${mode === 'light' ? 'Light' : 'Dark'} mode logo removed`);
      fetchSites();
    } catch {}
  };

  const getLogoUrl = (path) => path ? `${API_URL}/api/files/${path}` : null;
  const logoSite = sites.find(s => s._id === showLogoDialog);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="sites-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Project Sites</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your construction projects</p>
        </div>
        {['admin', 'manager'].includes(user?.role) && (
          <Button className="gap-1.5" onClick={() => { setForm({ name: '', location: '', description: '' }); setEditing(null); setShowForm(true); }} data-testid="add-site-btn">
            <Plus className="w-4 h-4" /> Add Site
          </Button>
        )}
      </div>

      {sites.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
          <h3 className="font-semibold mb-1">No sites yet</h3>
          <p className="text-sm text-muted-foreground">Add your first project site to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map(site => (
            <div key={site._id} className="bg-card border border-border p-6 space-y-4 hover:border-foreground/20 transition-colors" data-testid={`site-card-${site._id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {(site.logo_light || site.logo_dark) ? (
                    <img
                      src={getLogoUrl(site.logo_light || site.logo_dark)}
                      alt={site.name}
                      className="w-10 h-10 object-contain rounded border border-border"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-muted rounded border border-border flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-lg tracking-tight">{site.name}</h3>
                    {site.location && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {site.location}</p>
                    )}
                  </div>
                </div>
                {['admin', 'manager'].includes(user?.role) && (
                  <div className="flex gap-1">
                    <button onClick={() => setShowLogoDialog(site._id)} className="p-1.5 hover:bg-muted rounded" title="Manage Logo" data-testid={`logo-site-${site._id}`}><Image className="w-3.5 h-3.5" /></button>
                    <button onClick={() => openEdit(site)} className="p-1.5 hover:bg-muted rounded" data-testid={`edit-site-${site._id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(site._id)} className="p-1.5 hover:bg-muted rounded text-red-600" data-testid={`delete-site-${site._id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
              {site.description && <p className="text-sm text-muted-foreground">{site.description}</p>}
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Money In</div>
                  <div className="font-mono text-sm font-medium text-emerald-700 dark:text-emerald-400">{'\u20B9'}{(site.total_in || 0).toLocaleString('en-IN')}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Money Out</div>
                  <div className="font-mono text-sm font-medium text-red-700 dark:text-red-400">{'\u20B9'}{(site.total_out || 0).toLocaleString('en-IN')}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Balance</div>
                  <div className={`font-mono text-sm font-medium ${(site.balance || 0) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{'\u20B9'}{(site.balance || 0).toLocaleString('en-IN')}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Site Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Site' : 'Add New Site'}</DialogTitle>
            <DialogDescription className="sr-only">{editing ? 'Edit site details' : 'Create a new site'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="site-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Site Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="mt-1.5" data-testid="site-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Location</Label>
              <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="mt-1.5" data-testid="site-location-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1.5" data-testid="site-description-input" />
            </div>
            <Button type="submit" className="w-full" data-testid="site-submit-btn">{editing ? 'Update Site' : 'Create Site'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Logo Management Dialog */}
      <Dialog open={!!showLogoDialog} onOpenChange={() => setShowLogoDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Site Logo — {logoSite?.name}</DialogTitle>
            <DialogDescription>Upload separate logos for light and dark mode</DialogDescription>
          </DialogHeader>
          <div className="space-y-6" data-testid="logo-dialog">
            {/* Light Mode Logo */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Sun className="w-4 h-4" /> Light Mode Logo
              </div>
              {logoSite?.logo_light ? (
                <div className="flex items-center gap-3 p-3 bg-white border border-border rounded">
                  <img src={getLogoUrl(logoSite.logo_light)} alt="Light logo" className="h-12 w-auto object-contain" />
                  <Button variant="outline" size="sm" onClick={() => handleLogoDelete('light')} className="ml-auto gap-1" data-testid="delete-logo-light">
                    <X className="w-3 h-3" /> Remove
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={e => handleLogoUpload(e.target.files[0], 'light')}
                    disabled={uploadingLight}
                    data-testid="upload-logo-light"
                  />
                  {uploadingLight && <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
                </div>
              )}
            </div>

            {/* Dark Mode Logo */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Moon className="w-4 h-4" /> Dark Mode Logo
              </div>
              {logoSite?.logo_dark ? (
                <div className="flex items-center gap-3 p-3 bg-zinc-900 border border-border rounded">
                  <img src={getLogoUrl(logoSite.logo_dark)} alt="Dark logo" className="h-12 w-auto object-contain" />
                  <Button variant="outline" size="sm" onClick={() => handleLogoDelete('dark')} className="ml-auto gap-1" data-testid="delete-logo-dark">
                    <X className="w-3 h-3" /> Remove
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={e => handleLogoUpload(e.target.files[0], 'dark')}
                    disabled={uploadingDark}
                    data-testid="upload-logo-dark"
                  />
                  {uploadingDark && <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
                </div>
              )}
            </div>

            <p className="text-xs text-muted-foreground">PNG, SVG, or JPG recommended. Max 5MB. Logo appears in the sidebar.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
